import * as FortniteProfileHelper from './helpers/FortniteProfileHelper';
import * as LolProfileHelper from './helpers/LolProfileHelper';
import * as RocketLeagueProfileHelper from './helpers/RocketLeagueProfileHelper';
import * as ProfileHelpers from './helpers/ProfileHelpers';
import { GAMES } from '../../../mixins/games.commons';
import { PROFILE_KEYS } from '../../../mixins/profile.commons';

// Game helper map
const gameProfileHelpers = {
  [GAMES.LEAGUE_OF_LEGENDS]: LolProfileHelper,
  [GAMES.FORTNITE]: FortniteProfileHelper,
  [GAMES.ROCKET_LEAGUE]: RocketLeagueProfileHelper,
};

export const getters = {

  // state.gameList selectors
  getGameList: (state) => {
    return state.gameList;
  },

  getConnectedGames: (state) => {
    return state.gameList.filter(game => game.connected);
  },

  getSelectedGameData: (state) => {
    const game = state.gameList.find(game => game.alias === state.statistics.selectedGame);
    if (!game) {
      return {};
    }
    return game;
  },

  // state.profileStatusMap selectors
  getConnectedGamesLoading: (state) => {
    return state.profileStatusMap.getConnectedGames.loading;
  },

  getActivityStatsStatus: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    return state.profileStatusMap.statistics[game][profileKey].getActivityStats;
  },

  getGeneralStatsStatus: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    return state.profileStatusMap.statistics[game][profileKey].getGeneralStats;
  },

  getRankStatsStatus: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    return state.profileStatusMap.statistics[game][profileKey].getRankStats;
  },

  getProfileGeneralDataStatus: (state) => (profileKey) => {
    return state.profileStatusMap.general[profileKey];
  },

  getSelectedGameStatsStatus: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    return state.profileStatusMap.statistics[game][profileKey].getAllStats;
  },

  getLeaderboardStatus: (state) => {
    const game = state.statistics.selectedGame;
    return state.profileStatusMap.statistics[game].leaderboard.getLeaderboard;
  },

  searchPlayersStatus: (state) => {
    const game = state.statistics.selectedGame;
    return state.profileStatusMap.statistics[game].compare.searchPlayers;
  },

  // state.statistics selectors
  getSelectedGameName: (state) => {
    return state.statistics.selectedGame;
  },

  getSelectedGameStats: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    return state.statistics[game][profileKey];
  },

  getFeaturedStats: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    const profileGameStatistics = state.statistics[game][profileKey];
    const profileGeneralData = state.general[profileKey];
    if (!profileGameStatistics || !profileGeneralData) {
      return;
    }
    return gameProfileHelpers[game].getFeaturedStatisticsData(profileGameStatistics, profileGeneralData);
  },

  getFeaturedStatsDiff: (state) => {
    const game = state.statistics.selectedGame;
    const currentProfileGameStatistics = state.statistics[game][PROFILE_KEYS.CURRENT];
    const opponentProfileGameStatistics = state.statistics[game][PROFILE_KEYS.OPPONENT];
    if (!currentProfileGameStatistics || !opponentProfileGameStatistics) {
      return;
    }
    const current = gameProfileHelpers[game].getFeaturedStatisticsData(currentProfileGameStatistics);
    const opponent = gameProfileHelpers[game].getFeaturedStatisticsData(opponentProfileGameStatistics);

    // Note theres nothing to compare yet in this component.
    return {
      current,
      opponent,
    };
  },
  getRankStats: (state, getter, globalState) => (profileKey) => {
    const game = state.statistics.selectedGame;
    const gameStats = state.statistics[game][profileKey];
    if (!gameStats || !state.general[profileKey] || !gameStats.rank) {
      return;
    }

    const stats = gameProfileHelpers[game].getRankStats(gameStats, state.general[profileKey]);
    return {
      avatar: state.general[profileKey].avatar,
      ...stats,
    };
  },

  getOverviewStats: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    const gameStats = state.statistics[game][profileKey];
    if (!gameStats || !gameStats.general) {
      return;
    }

    const overviewStats = gameProfileHelpers[game].getOverviewStats(gameStats);
    return { ...overviewStats };
  },
  getOverviewStatsDiff: (state) => {
    const game = state.statistics.selectedGame;
    const currentGameStats = state.statistics[game][PROFILE_KEYS.CURRENT];
    const opponentGameStats = state.statistics[game][PROFILE_KEYS.OPPONENT];
    if (!currentGameStats || !currentGameStats.general || !opponentGameStats || !opponentGameStats.general) {
      return;
    }
    const current = gameProfileHelpers[game].getOverviewStats(currentGameStats);
    const opponent = gameProfileHelpers[game].getOverviewStats(opponentGameStats);

    if (game === GAMES.ROCKET_LEAGUE) {
      return {
        rating: ProfileHelpers.calculateDiffPercent(current.rating, opponent.rating),
        ratingPercentile: ProfileHelpers.calculateDiffPercent(current.ratingPercentile, opponent.ratingPercentile),
      };
    }

    return {
      wins: ProfileHelpers.calculateDiffPercent(current.wins, opponent.wins),
      winRatio: ProfileHelpers.calculateDiffPercent(current.winRatio, opponent.winRatio),
    };
  },

  getQueuesStats: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    const gameStats = state.statistics[game][profileKey];
    if (!gameStats || !gameStats.queues) {
      return;
    }

    const queuesMap = gameStats.queues;
    const queues = gameProfileHelpers[game].getQueuesStats(queuesMap);

    return { queues };
  },
  getQueuesStatsDiff: (state) => {
    const game = state.statistics.selectedGame;
    const currentGameStats = state.statistics[game][PROFILE_KEYS.CURRENT];
    const opponentGameStats = state.statistics[game][PROFILE_KEYS.OPPONENT];
    if (!currentGameStats || !currentGameStats.queues || !opponentGameStats || !opponentGameStats.queues) {
      return;
    }

    const current = gameProfileHelpers[game].getQueuesStats(currentGameStats.queues);
    const opponent = gameProfileHelpers[game].getQueuesStats(opponentGameStats.queues);
    const diffQueues = current.map(currentQueue => {
      const diffQueue = {};
      const opponentQueue = opponent.find(queue => queue.type === currentQueue.type);
      if (!opponentQueue) {
        return;
      }
      for (const [key, value] of Object.entries(currentQueue)) {
        const opponentValue = opponentQueue[key];
        if (typeof value === 'number' && opponentValue && typeof opponentValue === 'number') {
          diffQueue[key] = (opponentValue - value).toFixed();
        }
        if (key === 'stats') {
          const currentStats = value;
          diffQueue[key] = currentStats.map(stat => {
            const opponentStat = opponentValue.find(opponentStat => opponentStat.name === stat.name);
            stat.value = (opponentStat.value - stat.value).toFixed();
            return stat;
          });
        }
      }
      return diffQueue;
    }).filter(queue => !!queue);
    return {
      queues: diffQueues,
    };
  },

  getMonthlyStats: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    const gameStats = state.statistics[game][profileKey];
    if (!gameStats || !gameStats.activity || !gameStats.general) {
      return;
    }

    const activityData = gameProfileHelpers[game].getMonthlyStats(gameStats);
    return { activity: activityData };
  },
  getMonthlyStatsdiff: (state) => {
    const game = state.statistics.selectedGame;
    const currentGameStats = state.statistics[game][PROFILE_KEYS.CURRENT];
    const opponentGameStats = state.statistics[game][PROFILE_KEYS.OPPONENT];
    if (!currentGameStats || !currentGameStats.activity || !currentGameStats.general ||
      !opponentGameStats || !opponentGameStats.activity || !opponentGameStats.general) {
      return;
    }

    const current = gameProfileHelpers[game].getMonthlyStats(currentGameStats);
    const opponent = gameProfileHelpers[game].getMonthlyStats(opponentGameStats);
    const diffMonthHoursPercent = ProfileHelpers.calculateDiffPercent(current.month.hoursPlayed, opponent.month.hoursPlayed);

    return {
      performance: opponent.month.performancePercentage - current.month.performancePercentage,
      monthHoursPercent: diffMonthHoursPercent.toFixed(),
      week: opponent.week.hoursPlayed - current.week.hoursPlayed,
      month: opponent.month.hoursPlayed - current.month.hoursPlayed,
    };
  },

  getRankProgression: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    const gameStats = state.statistics[game][profileKey];
    if (!gameStats || !gameStats.rankProgression) {
      return;
    }

    const graphData = gameProfileHelpers[game].getRankProgression(gameStats);
    return graphData;
  },
  getRankProgressionDiff: (state) => {
    const game = state.statistics.selectedGame;
    const currentGameStats = state.statistics[game][PROFILE_KEYS.CURRENT];
    const opponentGameStats = state.statistics[game][PROFILE_KEYS.OPPONENT];
    if (!currentGameStats || !currentGameStats.rankProgression || !opponentGameStats || !opponentGameStats.rankProgression) {
      return;
    }
    const current = gameProfileHelpers[game].getRankProgression(currentGameStats);
    const opponent = gameProfileHelpers[game].getRankProgression(opponentGameStats);

    const currentFirstPoints = current.points[0];
    const currentLatestPoints = current.points[current.points.length - 1];
    const currentDiff = currentFirstPoints - currentLatestPoints;
    const opponentFirstPoints = opponent.points[0];
    const opponentLatestPoints = opponent.points[opponent.points.length - 1];
    const opponentDiff = opponentFirstPoints - opponentLatestPoints;

    return {
      progressionDiff: currentDiff - opponentDiff,
    };
  },

  getCoreStats: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    const gameStats = state.statistics[game][profileKey];
    if (!gameStats || !gameStats.general) {
      return;
    }

    const { maxGraphValue, stats } = gameProfileHelpers[game].getCoreStats(gameStats);
    return { maxGraphValue, stats };
  },
  getCoreStatsDiff: (state) => {
    const game = state.statistics.selectedGame;
    const currentGameStats = state.statistics[game][PROFILE_KEYS.CURRENT];
    const opponentGameStats = state.statistics[game][PROFILE_KEYS.OPPONENT];
    if (!currentGameStats || !currentGameStats.general || !opponentGameStats || !opponentGameStats.general) {
      return;
    }

    const current = gameProfileHelpers[game].getCoreStats(currentGameStats);
    const opponent = gameProfileHelpers[game].getCoreStats(opponentGameStats);
    const overall = gameProfileHelpers[game].getCoreStatsDiff(current, opponent);

    return {
      overall,
    };
  },

  getPlayerName: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    const gameStats = state.statistics[game][profileKey];
    if (!gameStats) {
      return;
    }

    return gameStats.general.playerName;
  },

  currentBoomId: (state) => {
    return state.currentBoomId;
  },

  opponentBoomId: (state) => {
    return state.opponentBoomId;
  },

  getPlayerList: (state) => {
    const game = state.statistics.selectedGame;
    const compare = state.statistics[game].compare;
    if (!compare || !compare.playerList) {
      return [];
    }
    return compare.playerList;
  },

  getLeaderboardList: (state) => {
    const game = state.statistics.selectedGame;
    const leaderboard = state.statistics[game].leaderboard;
    if (!leaderboard || !leaderboard.list) {
      return [];
    }

    return gameProfileHelpers[game].getleaderboardListWithStatName(leaderboard.list);
  },

  getLeaderboardTop3: (state) => {
    const game = state.statistics.selectedGame;
    const leaderboard = state.statistics[game].leaderboard;
    if (!leaderboard || !leaderboard.list) {
      return [];
    }
    return gameProfileHelpers[game].getleaderboardListWithStatName(leaderboard.list.slice(0, 3));
  },

  getLeaderboardCurrentUserStats: (state) => {
    const game = state.statistics.selectedGame;
    const leaderboard = state.statistics[game].leaderboard;
    if (!leaderboard || !leaderboard.current) {
      return;
    }
    const current = gameProfileHelpers[game].getleaderboardListWithStatName([leaderboard.current]);
    return current[0];
  },

  getLeaderboardSelectedFilters: (state) => {
    const game = state.statistics.selectedGame;
    const leaderboard = state.statistics[game].leaderboard;
    if (!leaderboard || !leaderboard.selectedFilters) {
      return;
    }
    return leaderboard.selectedFilters;
  },

  getLeaderboardOffset: (state) => {
    const game = state.statistics.selectedGame;
    return state.statistics[game].leaderboard.offset;
  },

  hasGameStats: (state) => (profileKey) => {
    const game = state.statistics.selectedGame;
    return !!state.statistics[game][profileKey];
  },

};
